import { useSyncExternalStore } from "react";

/**
 * Subscribes to a CSS media query. Uses useSyncExternalStore so the first
 * render already has the right value (no desktop → mobile flash on load).
 */
export default function useMediaQuery(query) {
  const subscribe = (notify) => {
    if (typeof window === "undefined" || !window.matchMedia) return () => {};
    const mql = window.matchMedia(query);
    // Safari < 14 only knows addListener.
    if (mql.addEventListener) mql.addEventListener("change", notify);
    else mql.addListener(notify);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", notify);
      else mql.removeListener(notify);
    };
  };
  const getSnapshot = () =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia(query).matches
      : false;
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

/** Phone-sized layouts: matches Tailwind's `md` breakpoint (< 768px). */
export const MOBILE_QUERY = "(max-width: 767px)";

export function useIsMobile() {
  return useMediaQuery(MOBILE_QUERY);
}

/** True on devices where a finger (not a mouse) is available — phones, tablets, touch laptops. */
export function useHasTouch() {
  return useMediaQuery("(any-pointer: coarse)");
}

/** True when the *primary* input is a finger (phones, tablets) — touch laptops with a mouse stay false. */
export function useCoarsePointer() {
  return useMediaQuery("(pointer: coarse)");
}

/** True when the app was launched from the home screen (no browser chrome). */
export function useIsStandalone() {
  const standalone = useMediaQuery("(display-mode: standalone)");
  const iosStandalone =
    typeof navigator !== "undefined" && navigator.standalone === true;
  return standalone || iosStandalone;
}
